import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Send, MapPin, Mountain, Crosshair, PawPrint, Info, ArrowRight, ChevronRight, BadgeCheck,
  Map as MapIcon, ClipboardList, Backpack, CloudSun, ChefHat, BookOpen,
  Landmark, Building2, Star, Loader2,
} from "lucide-react";
import { api } from "@/lib/api";
import { useUI } from "@/context/UIContext";
import { useAuth } from "@/context/AuthContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";
import { MarkdownLite } from "@/components/MarkdownLite";
import { STATE_HERO, STATE_HERO_DEFAULT } from "@/lib/stateMeta";
import { MEDIA } from "@/lib/media";

const HERO = "https://images.unsplash.com/photo-1615900304401-923d2dfd8beb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1900&fit=crop";

const CAPABILITIES = [
  { icon: MapIcon, title: "Hunt Planning", desc: "Plan smarter with unit recommendations, odds, deadlines, and strategy.", img: MEDIA.stateHero, to: "/explore" },
  { icon: ClipboardList, title: "Regulations & Tags", desc: "Get up-to-date rules, seasons, bag limits, and tag info for any state.", img: MEDIA.exploreRegs, to: "/explore" },
  { icon: Backpack, title: "Gear Advice", desc: "Personalized gear recommendations based on your hunt and budget.", img: MEDIA.gear.packs, to: "/marketplace" },
  { icon: CloudSun, title: "Conditions", desc: "Live weather, terrain, fire danger, and other key conditions.", img: "https://images.unsplash.com/photo-1784813488967-8525683d6c14?crop=entropy&cs=srgb&fm=jpg&q=85&w=900&fit=crop", to: "/explore" },
  { icon: ChefHat, title: "From Field to Table", desc: "Wild game recipes, butchering tips, and cooking techniques.", img: MEDIA.exploreRecipes, to: "/recipes" },
  { icon: BookOpen, title: "Learn & Improve", desc: "Get answers, tips, and strategies to become a better hunter.", img: "https://images.unsplash.com/photo-1754388382209-c119eaf9bcb0?crop=entropy&cs=srgb&fm=jpg&q=85&w=900&fit=crop", to: "/field-notes" },
];

const SOURCES = [
  { icon: Landmark, label: "Official state wildlife agencies" },
  { icon: Building2, label: "USDA & government data" },
  { icon: MapIcon, label: "Trusted hunting & mapping resources" },
  { icon: Star, label: "Gear reviews & real hunter feedback" },
];

function ago(iso) {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  const d = Math.round(s / 86400);
  return d === 1 ? "Yesterday" : `${d} days ago`;
}

// Real hunt-completion %: 6 binary milestones + a FRACTIONAL gear score (owned/total),
// so readiness rises gradually as the gear locker fills (mirrors the Hunt Dashboard).
function readinessOf(h) {
  if (!h) return 0;
  const gearFrac = h.gear_total ? (h.gear_owned || 0) / h.gear_total : (h.gear_complete ? 1 : 0);
  const binary = [true, !!h.jurisdiction, !!h.method, !!h.area, !!h.draw_status, !!h.start_date];
  return Math.round(((binary.filter(Boolean).length + gearFrac) / 7) * 100);
}

function suggestionsFor(h) {
  if (!h) return [
    "Best states for a first elk hunt", "What gear do I need for backcountry hunting?",
    "How do draw odds and points work?", "Compare bow vs rifle for elk",
    "What should I pack for a 7-day hunt?", "How do I field-dress big game?",
  ];
  const sp = h.species || "Elk";
  const spl = sp.toLowerCase();
  const st = h.jurisdiction || "your state";
  const m = (h.method || "").toLowerCase();
  const weaponWord = m.includes("arch") ? "archery" : m.includes("rifle") ? "rifle" : (h.method || "your weapon").toLowerCase();
  const bow = m.includes("arch") ? "bow" : "rifle";
  const days = h.duration_days || 7;
  const article = /^[aeiou]/i.test(sp) ? "an" : "a";
  return [
    `Best ${spl} units in ${st} for ${weaponWord}`,
    `What should I pack for a ${days}-day hunt?`,
    h.area ? `Current weather in ${h.area}` : `Current weather in the ${st} backcountry`,
    `Compare ${bow} setups for ${spl} hunting`,
    `${sp} draw odds for 2026`,
    `How do I quarter ${article} ${spl} in the field?`,
  ];
}

const Chip = ({ icon: Ic, label, testid }) => (
  <div className="inline-flex items-center gap-2 border border-bone/25 rounded-md px-3 py-2 text-bone/80" data-testid={testid}>
    <Ic size={15} className="text-blaze" />
    <span className="hr-overline text-[11px] tracking-wide">{label}</span>
  </div>
);

export default function HuntAssistant() {
  const navigate = useNavigate();
  const { setAiContext, openHunt, setBuildHuntOpen, gearOpen, setGearOpen } = useUI();
  const { user, isAuthed, ready } = useAuth();
  const [hunts, setHunts] = useState([]);
  const [active, setActive] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [histExpanded, setHistExpanded] = useState(false);
  const chatRef = useRef(null);
  const gearWasOpen = useRef(false);

  const firstName = (user?.display_name || "").trim().split(" ")[0] || "there";

  useEffect(() => {
    if (!ready) return;
    if (!isAuthed) { setLoading(false); return; }
    let live = true;
    (async () => {
      try {
        const list = (await api.hunts()).slice().sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
        if (!live) return;
        setHunts(list);
        const savedId = localStorage.getItem("hr_active_hunt");
        const act = list.find((h) => h.id === savedId) || list[0] || null;
        setActive(act);
        if (act) setAiContext({ state: act.jurisdiction, species: act.species, weapon: act.method, hunt_style: act.hunt_style, unit: act.area || undefined, page: "hunt-assistant" });
      } catch { /* ignore */ }
      try { const h = await api.aiHistory(); if (live) setHistory(h || []); } catch { /* ignore */ }
      if (live) setLoading(false);
    })();
    return () => { live = false; };
  }, [ready, isAuthed, setAiContext]);

  const suggestions = useMemo(() => suggestionsFor(active), [active]);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages, busy]);

  // When the Gear Locker modal closes, refresh hunts so gear-aware progress updates live.
  useEffect(() => {
    if (gearOpen) { gearWasOpen.current = true; return; }
    if (gearWasOpen.current && isAuthed) {
      gearWasOpen.current = false;
      api.hunts()
        .then((list) => setHunts(list.slice().sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")))))
        .catch(() => {});
    }
  }, [gearOpen, isAuthed]);

  const ask = async (q) => {
    const query = (q ?? input).trim();
    if (!query || busy) return;
    const pc = active
      ? { state: active.jurisdiction, species: active.species, weapon: active.method, hunt_style: active.hunt_style, unit: active.area || undefined, page: "hunt-assistant" }
      : { page: "hunt-assistant" };
    setAiContext(pc);
    setMessages((m) => [...m, { role: "user", text: query }]);
    setInput("");
    setBusy(true);
    try {
      const data = await api.askAI({ query, hunt_id: active?.id, page_context: pc }, true);
      setMessages((m) => [...m, { role: "ai", text: data.response, links: data.linked_hunts || [], sources: data.official_resources || [] }]);
      api.aiHistory().then((h) => setHistory(h || [])).catch(() => {});
    } catch {
      setMessages((m) => [...m, { role: "ai", text: "I couldn't reach HomeRange AI just now. Please try again." }]);
    } finally {
      setBusy(false);
    }
  };

  if (ready && !isAuthed) {
    return (
      <div className="bg-[#0d1410] min-h-screen">
        <SiteHeader />
        <div className="grid place-items-center py-40 text-center px-6" data-testid="ha-signin-gate">
          <div>
            <span className="hr-overline text-blaze">HomeRange AI</span>
            <h1 className="font-serif text-4xl sm:text-5xl text-bone mt-2">Your hunting assistant.</h1>
            <p className="text-bone/60 mt-3 max-w-md mx-auto">Sign in to plan a hunt and get answers personalized to your species, state, gear, and conditions.</p>
            <button onClick={() => setBuildHuntOpen(true)} className="mt-6 bg-blaze text-white px-6 py-3 rounded-md font-semibold inline-flex items-center gap-2" data-testid="ha-build-cta">Build My Hunt <ArrowRight size={16} /></button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-[#0d1410] min-h-screen text-bone overflow-x-hidden" data-testid="hunt-assistant-page">
      <SiteHeader />

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden border-b border-bone/10">
        <div className="absolute inset-0">
          <img src={HERO} alt="Hunter in the mountains at sunrise" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d1410] via-[#0d1410]/80 to-[#0d1410]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d1410] via-transparent to-[#0d1410]/50" />
        </div>

        <div className="relative max-w-[1360px] mx-auto px-5 sm:px-8 py-12 lg:py-16 grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* LEFT COLUMN */}
          <div className="max-w-xl">
            <span className="hr-overline text-blaze">HomeRange AI</span>
            <h1 className="font-serif text-6xl lg:text-7xl leading-[0.92] mt-3">Your hunting<br />assistant.</h1>
            <p className="text-bone/70 text-base leading-relaxed mt-5 max-w-md">
              Ask anything about hunting, regulations, gear, locations, or strategy. HomeRange AI uses trusted sources to give you answers you can count on.
            </p>

            {/* ACTIVE CONTEXT */}
            <div className="border border-bone/15 rounded-lg bg-[#0d1410]/60 backdrop-blur-sm p-5 mt-8" data-testid="ha-active-context">
              <p className="hr-overline text-bone/45 mb-3">Active Context</p>
              {active ? (
                <div className="flex flex-wrap gap-2.5">
                  <Chip icon={PawPrint} label={active.species} testid="ha-ctx-species" />
                  <Chip icon={MapPin} label={active.jurisdiction} testid="ha-ctx-state" />
                  <Chip icon={Crosshair} label={active.method} testid="ha-ctx-method" />
                  <Chip icon={Mountain} label={active.hunt_style} testid="ha-ctx-style" />
                </div>
              ) : (
                <p className="text-sm text-bone/50">No active hunt yet. Build one to personalize your assistant.</p>
              )}
              <div className="flex items-center justify-between mt-4">
                <span className="text-xs text-bone/50">Personalized to your hunt &amp; gear</span>
                <button onClick={() => (active ? openHunt(active.id) : setBuildHuntOpen(true))} className="text-xs font-semibold text-blaze hover:underline underline-offset-4" data-testid="ha-manage">Manage</button>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN — AI PANEL (inline chat) */}
          <div className="border border-bone/15 rounded-lg bg-[#0b110d]/80 backdrop-blur-md p-6 sm:p-8 lg:mt-6 flex flex-col" data-testid="ha-ai-panel">
            <p className="text-blaze text-sm font-medium" data-testid="ha-greeting">Hi {firstName},</p>
            <h2 className="font-serif text-3xl sm:text-4xl leading-tight mt-1.5">What can I help you plan, find, or understand today?</h2>

            {messages.length > 0 && (
              <div ref={chatRef} className="mt-5 max-h-[300px] overflow-y-auto hr-scroll space-y-3 pr-1" data-testid="ha-chat">
                {messages.map((m, i) => (
                  <div key={i} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                    <div className={`px-4 py-3 rounded-lg text-sm leading-relaxed max-w-[92%] ${m.role === "user" ? "bg-blaze text-white whitespace-pre-wrap" : "bg-[#0d1410] border border-bone/12 text-bone/90"}`} data-testid={`ai-message-${m.role}`}>
                      {m.role === "ai" ? <MarkdownLite text={m.text} /> : m.text}
                    </div>
                    {m.role === "ai" && m.sources?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5" data-testid="ha-ai-sources">
                        {m.sources.slice(0, 4).map((s, k) => (
                          <a key={k} href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[11px] border border-bone/15 rounded-full px-2.5 py-1 text-bone/70 hover:border-blaze hover:text-blaze transition-colors" data-testid="ha-ai-source">
                            <BadgeCheck size={11} className="text-blaze" /> {s.agency || s.title}
                          </a>
                        ))}
                      </div>
                    )}
                    {m.role === "ai" && m.links?.length > 0 && (
                      <div className="mt-2 w-full space-y-1.5">
                        {m.links.map((l, j) => (
                          <button key={j} onClick={() => navigate(`/states/${encodeURIComponent(l.state || active?.jurisdiction || "")}/species/${encodeURIComponent(l.species || active?.species || "")}`)}
                            className="w-full text-left text-xs border border-bone/15 rounded-md px-3 py-2 text-bone/80 hover:border-blaze transition-colors" data-testid="ha-ai-link">
                            {l.label || `${l.species} in ${l.state}`}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {busy && <div className="flex items-center gap-2 text-sm text-bone/50"><Loader2 size={15} className="animate-spin text-blaze" /> HomeRange AI is thinking…</div>}
              </div>
            )}

            <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="grid grid-cols-[1fr_auto] gap-3 mt-6">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask HomeRange AI anything…"
                className="bg-[#0d1410]/70 border border-bone/20 rounded-md px-4 py-4 text-sm text-bone placeholder:text-bone/40 outline-none focus:border-blaze"
                data-testid="ha-ai-input"
              />
              <button type="submit" disabled={busy} className="bg-blaze hover:bg-blaze-light transition-colors text-white w-14 rounded-md grid place-items-center disabled:opacity-60" aria-label="Ask" data-testid="ha-ai-send">
                {busy ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
              </button>
            </form>

            {messages.length === 0 && (
              <div className="flex flex-wrap gap-2.5 mt-4" data-testid="ha-suggestions">
                {suggestions.map((s) => (
                  <button key={s} onClick={() => ask(s)} className="border border-bone/20 rounded-md px-3.5 py-2 text-[13px] text-bone/75 hover:border-blaze hover:text-blaze transition-colors" data-testid="ha-suggestion">{s}</button>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between mt-7 pt-4 border-t border-bone/10">
              <span className="text-xs text-bone/45">Powered by verified data. Always check official regulations.</span>
              <button onClick={() => navigate("/explore")} className="text-xs text-blaze inline-flex items-center gap-1.5 hover:underline underline-offset-4" data-testid="ha-how-ai-works">How AI works <Info size={13} /></button>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CAPABILITIES ============ */}
      <section className="max-w-[1360px] mx-auto px-5 sm:px-8 py-12">
        <div className="flex items-center justify-between mb-6">
          <h3 className="hr-overline text-bone/50">What can HomeRange AI do?</h3>
          <button onClick={() => navigate("/explore")} className="text-xs text-blaze inline-flex items-center gap-1.5 hover:underline underline-offset-4" data-testid="ha-view-capabilities">View all capabilities <ArrowRight size={13} /></button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {CAPABILITIES.map(({ icon: Ic, title, desc, img, to }) => {
            const isGear = title === "Gear Advice";
            const dest = title === "Conditions" && active ? `/conditions/${encodeURIComponent(active.jurisdiction)}`
              : (title === "Regulations & Tags" && active ? `/states/${encodeURIComponent(active.jurisdiction)}` : to);
            return (
            <button key={title} onClick={() => (isGear ? setGearOpen(true) : navigate(dest))} className="group relative rounded-lg overflow-hidden border border-bone/12 text-left min-h-[210px] flex flex-col justify-end p-4" data-testid={`ha-cap-${title.replace(/[^a-zA-Z]+/g, "-").toLowerCase()}`}>
              <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 group-hover:scale-105 transition-all duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d1410] via-[#0d1410]/70 to-[#0d1410]/20" />
              <div className="relative">
                <Ic size={22} className="text-bone/90 mb-3" strokeWidth={1.4} />
                <p className="hr-overline text-[11px] text-bone">{title}</p>
                <p className="text-[12px] text-bone/60 mt-1.5 leading-snug">{desc}</p>
              </div>
            </button>
            );
          })}
        </div>
      </section>

      {/* ============ LOWER: 3 COLUMNS ============ */}
      <section className="max-w-[1360px] mx-auto px-5 sm:px-8 pb-16 grid lg:grid-cols-3 gap-8 lg:gap-10 border-t border-bone/10 pt-10">
        {/* YOUR HUNTS */}
        <div data-testid="ha-your-hunts">
          <h3 className="hr-overline text-bone/50 mb-4">Your Hunts</h3>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-bone/40 py-4"><Loader2 size={15} className="animate-spin text-blaze" /> Loading…</div>
          ) : hunts.length === 0 ? (
            <div className="border border-dashed border-bone/20 rounded-lg p-5">
              <p className="text-sm text-bone/55">No hunts yet.</p>
              <button onClick={() => setBuildHuntOpen(true)} className="mt-3 bg-blaze text-white px-4 py-2.5 rounded-md text-sm font-semibold inline-flex items-center gap-2" data-testid="ha-build-first">Build My Hunt <ArrowRight size={15} /></button>
            </div>
          ) : (
            <div className="space-y-3">
              {hunts.map((h) => {
                const pct = readinessOf(h);
                const segs = 7;
                const filled = Math.round((pct / 100) * segs);
                return (
                  <button key={h.id} onClick={() => openHunt(h.id)} className="w-full flex items-center gap-4 border border-bone/12 rounded-lg p-3 hover:border-blaze/60 transition-colors text-left group" data-testid={`ha-hunt-${h.id}`}>
                    <div className="w-24 h-16 rounded-md overflow-hidden shrink-0">
                      <img src={STATE_HERO[h.jurisdiction] || STATE_HERO_DEFAULT} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-serif text-lg text-bone truncate">{h.title}</p>
                      <p className="text-[12px] text-bone/55 mt-0.5" data-testid={`ha-hunt-pct-${h.id}`}>{pct}% Complete</p>
                      <div className="flex gap-1 mt-2">
                        {Array.from({ length: segs }).map((_, i) => (
                          <span key={i} className={`h-1.5 flex-1 rounded-sm ${i < filled ? "bg-blaze" : "bg-bone/12"}`} />
                        ))}
                      </div>
                    </div>
                    <ChevronRight size={20} className="text-bone/40 group-hover:text-blaze transition-colors shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* RECENT QUESTIONS */}
        <div data-testid="ha-recent-questions">
          <h3 className="hr-overline text-bone/50 mb-4">Recent Questions</h3>
          {history.length === 0 ? (
            <div className="border border-dashed border-bone/20 rounded-lg p-5 text-sm text-bone/50">
              Your HomeRange AI conversations will appear here. Ask a question above to get started.
            </div>
          ) : (
            <div className="border border-bone/12 rounded-lg divide-y divide-bone/10">
              {history.slice(0, histExpanded ? 30 : 3).map((q, i) => (
                <button key={i} onClick={() => ask(q.query)} className="w-full flex items-center justify-between gap-4 px-4 py-3.5 hover:bg-bone/[0.03] transition-colors text-left" data-testid={`ha-history-${i}`}>
                  <span className="text-sm text-bone/85 truncate">{q.query}</span>
                  <span className="text-[11px] text-bone/40 shrink-0">{ago(q.created_at)}</span>
                </button>
              ))}
              {history.length > 3 && (
                <button onClick={() => setHistExpanded((v) => !v)} className="w-full text-left px-4 py-3 text-xs text-blaze inline-flex items-center gap-1.5 hover:underline underline-offset-4" data-testid="ha-view-history">{histExpanded ? "Show less" : "View all history"} <ArrowRight size={13} /></button>
              )}
            </div>
          )}
        </div>

        {/* AI SOURCES & TRUST */}
        <div data-testid="ha-sources">
          <h3 className="hr-overline text-bone/50 mb-4">AI Sources &amp; Trust</h3>
          <div className="space-y-3.5">
            {SOURCES.map(({ icon: Ic, label }) => (
              <div key={label} className="flex items-center gap-3 text-sm text-bone/80">
                <Ic size={17} className="text-blaze shrink-0" strokeWidth={1.5} />
                <span>{label}</span>
              </div>
            ))}
          </div>
          <button onClick={() => navigate("/explore")} className="mt-5 text-xs text-blaze inline-flex items-center gap-1.5 hover:underline underline-offset-4" data-testid="ha-sources-more">Learn more about our sources <ArrowRight size={13} /></button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
